import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { api } from '../servizi/api.ts'
import { useAree, useSessione } from '../servizi/sessione.tsx'
import {
  Bottone, Campo, Etichetta, EtichettaArea, Modale, Paginazione, Riquadro, Scelta, Spunta, Tabella,
} from '../componenti/base.tsx'
import { Attesa, Avviso, Intestazione, Vuoto } from '../componenti/Pezzi.tsx'
import { LogoCidis } from './LoghiStampa.tsx'
import { LIMITE, conteggio, usaElenco } from '../componenti/elenco.tsx'
import { IconaPiu } from '../componenti/Icone.tsx'
import { SceltaComune, type ComuneScelto } from './Ente.tsx'
import { ModuloProgetto, STATI_PROGETTO, StatoDelProgetto, dataBreve, messaggioDi, type StatoProgetto } from './Progetto.tsx'
import { opzioniSedi } from '../servizi/sedi.ts'

/**
 * Elenco dei progetti (D-24) con i filtri per stato calcolato dalle date, fondo, area principale,
 * sede CIDIS, comune e testo. I filtri stanno nell'indirizzo della pagina: un elenco filtrato si puo'
 * condividere. In coda la pagina del catalogo dei fondi, che l'amministratore amplia.
 */

const CHIAVI_FILTRO = ['cerca', 'stato', 'fondo', 'area', 'sede', 'comune'] as const
type ChiaveFiltro = (typeof CHIAVI_FILTRO)[number]

type RigaProgetto = {
  id: string; codice_cidis: string | null; codice_finanziatore: string | null; titolo: string
  fondo_codice: string | null; fondo_nome: string | null; fondo_testo: string | null; fondo_storico: string | null
  area_principale: string | null; data_inizio: string | null; data_fine: string | null; stato: StatoProgetto | null
  capofila: string | null; capofila_sigla: string | null; sedi: { id: string; nome: string }[]; wp: number
}
type VoceFondo = { codice: string; nome: string; richiede_testo: boolean; attivo: boolean; ordinamento: number }

/** Tutte le voci del catalogo dei fondi, comprese quelle non piu' in uso. */
function useFondi(versione = 0) {
  const [voci, setVoci] = useState<VoceFondo[] | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  useEffect(() => {
    let valido = true
    api.get<VoceFondo[]>('/riferimenti/cataloghi/fondo')
      .then((r) => { if (valido) { setVoci(r); setErrore(null) } })
      .catch((e) => { if (valido) setErrore(messaggioDi(e, 'Catalogo dei fondi non disponibile.')) })
    return () => { valido = false }
  }, [versione])
  return { voci, errore }
}

export function Progetti() {
  const { puo, sedi, tassonomia } = useSessione()
  const aree = useAree()
  const fondi = useFondi()
  const naviga = useNavigate()
  const [parametri, setParametri] = useSearchParams()
  const [creazione, setCreazione] = useState(false)
  const [testo, setTesto] = useState(() => parametri.get('cerca') ?? '')
  const [comune, setComune] = useState<ComuneScelto | null>(null)

  const filtro = (chiave: ChiaveFiltro) => parametri.get(chiave) ?? ''
  const aggiorna = useCallback((chiave: ChiaveFiltro, valore: string) => {
    setParametri((attuali) => {
      const nuovi = new URLSearchParams(attuali)
      if (valore) nuovi.set(chiave, valore); else nuovi.delete(chiave)
      return nuovi
    }, { replace: true })
  }, [setParametri])

  // Il testo passa nell'indirizzo dopo una pausa nella digitazione: ogni lettura dell'elenco resta nel
  // registro eventi, e una per tasto non serve a nessuno.
  const cercaInUso = parametri.get('cerca') ?? ''
  useEffect(() => {
    const termine = testo.trim()
    if (termine === cercaInUso) return
    const attesa = setTimeout(() => aggiorna('cerca', termine), 350)
    return () => clearTimeout(attesa)
  }, [testo, cercaInUso, aggiorna])

  // Il filtro per comune arriva dall'indirizzo come codice ISTAT: il nome si recupera una volta.
  const comuneFiltrato = parametri.get('comune')
  useEffect(() => {
    if (!comuneFiltrato) { setComune(null); return }
    if (comune?.codice_istat === comuneFiltrato) return
    const ripiego = { codice_istat: comuneFiltrato, nome: comuneFiltrato, provincia_sigla: null }
    api.get<ComuneScelto[]>(`/riferimenti/comuni?cerca=${encodeURIComponent(comuneFiltrato)}&limite=5`)
      .then((r) => setComune(r.find((c) => c.codice_istat === comuneFiltrato) ?? ripiego))
      .catch(() => setComune(ripiego))
  }, [comuneFiltrato]) // eslint-disable-line react-hooks/exhaustive-deps

  const query = useMemo(() => {
    const q = new URLSearchParams()
    for (const chiave of CHIAVI_FILTRO) {
      const valore = parametri.get(chiave)
      if (valore) q.set(chiave, valore)
    }
    return q.toString()
  }, [parametri])
  const { dati, errore, finestra } = usaElenco<RigaProgetto>(`/progetti${query ? `?${query}` : ''}`)
  const conFiltri = CHIAVI_FILTRO.some((chiave) => parametri.get(chiave))

  function azzera() {
    setTesto('')
    setComune(null)
    setParametri(new URLSearchParams(), { replace: true })
  }

  return (
    <>
      <Intestazione
        titolo="Progetti"
        sottotitolo={conteggio(finestra.totale, 'progetto', 'progetti')}
        azioni={<>
          {puo('catalogo.gestisci') && <Link className="bottone bottone-discreto" to="/fondi">Catalogo dei fondi</Link>}
          {puo('progetto.gestisci') && (
            <Bottone variante="primario" icona={<IconaPiu />} onClick={() => setCreazione(true)}>Nuovo progetto</Bottone>
          )}
        </>}
      />

      <Riquadro titolo="Filtri" azioni={conFiltri
        ? <Bottone variante="discreto" dimensione="piccolo" onClick={azzera}>Azzera i filtri</Bottone>
        : undefined}>
        <div className="filtri-report">
          <Campo etichetta="Cerca" tipo="search" valore={testo} cambia={setTesto} segnaposto="Titolo, codice, CUP o CIG" />
          <Scelta etichetta="Stato" vuota="Tutti" valore={filtro('stato')} cambia={(v) => aggiorna('stato', v)}
                  opzioni={(Object.keys(STATI_PROGETTO) as StatoProgetto[])
                    .map((s) => ({ valore: s, etichetta: STATI_PROGETTO[s].etichetta }))} />
          <Scelta etichetta="Fondo" vuota="Tutti" valore={filtro('fondo')} cambia={(v) => aggiorna('fondo', v)}
                  opzioni={(fondi.voci ?? []).map((f) => ({ valore: f.codice, etichetta: f.attivo ? f.nome : `${f.nome} · non più in uso` }))} />
          <Scelta etichetta="Area principale" vuota="Tutte" valore={filtro('area')} cambia={(v) => aggiorna('area', v)}
                  opzioni={(tassonomia?.aree ?? []).map((a) => ({ valore: a.codice, etichetta: a.nome }))} />
          <Scelta etichetta="Sede CIDIS" vuota="Tutte" valore={filtro('sede')} cambia={(v) => aggiorna('sede', v)}
                  opzioni={opzioniSedi(sedi)} />
          <SceltaComune etichetta="Comune" vuota="Tutti i comuni" valore={comune}
                        cambia={(c) => { setComune(c); aggiorna('comune', c?.codice_istat ?? '') }} />
        </div>
      </Riquadro>

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!errore && !dati && <Attesa />}
      {dati && (
        <Riquadro denso>
          <Tabella intestazioni={['Progetto', 'Area', 'Periodo', 'Stato', 'Capofila', 'Sedi CIDIS', 'WP']}
                   vuoto={<Vuoto testo={conFiltri ? 'Nessun progetto con questi filtri fra quelli che puoi vedere.' : 'Nessun progetto registrato.'} />}>
            {dati.map((p) => (
              <tr key={p.id}>
                <td data-etichetta="Progetto">
                  <span className="dato-testo">
                    <Link to={`/progetti/${p.id}`} className="voce-principale">{p.titolo}</Link>
                    <span className="piccolo tenue">
                      {[p.codice_cidis, p.fondo_nome ?? p.fondo_storico].filter(Boolean).join(' · ') || '—'}
                    </span>
                  </span>
                </td>
                <td data-etichetta="Area">
                  {p.area_principale
                    ? <EtichettaArea codice={p.area_principale} colore={aree.coloreDi(p.area_principale)} />
                    : <span className="tenue">—</span>}
                </td>
                <td data-etichetta="Periodo" className="mono piccolo">{dataBreve(p.data_inizio)} – {dataBreve(p.data_fine)}</td>
                <td data-etichetta="Stato"><StatoDelProgetto stato={p.stato} /></td>
                <td data-etichetta="Capofila">
                  {p.capofila ? `${p.capofila_sigla ? `${p.capofila_sigla} — ` : ''}${p.capofila}` : <span className="tenue">—</span>}
                </td>
                <td data-etichetta="Sedi CIDIS">
                  {p.sedi.length ? p.sedi.map((s) => s.nome).join(', ') : <span className="tenue">—</span>}
                </td>
                <td data-etichetta="WP" className="mono">{p.wp}</td>
              </tr>
            ))}
          </Tabella>
          <Paginazione salta={finestra.salta} limite={LIMITE} totale={finestra.totale}
                       ricevute={dati.length} cambia={finestra.cambia} />
        </Riquadro>
      )}

      {/* Logo CIDIS di tutte le stampe (462): lo sostituisce la Direzione. */}
      {puo('logo.predefinito') && <LogoCidis />}

      {creazione && (
        <Modale titolo="Nuovo progetto" larga chiudi={() => setCreazione(false)}>
          <ModuloProgetto sezioni={['generali', 'periodo']} annulla={() => setCreazione(false)}
                          salvato={(id) => { setCreazione(false); naviga(`/progetti/${id}`) }} />
        </Modale>
      )}
    </>
  )
}

// --- catalogo dei fondi ------------------------------------------------------------------------------

/**
 * Catalogo dei fondi (D-24): la proposta iniziale si amplia, le etichette si correggono e le voci non
 * piu' in uso si disattivano. Una voce non si cancella, perche' i progetti che la usano la tengono. Lo
 * consulta chiunque; lo modifica chi ha `catalogo.gestisci`.
 */
export function CatalogoFondi() {
  const { puo } = useSessione()
  const [versione, setVersione] = useState(0)
  const { voci, errore } = useFondi(versione)
  const [modulo, setModulo] = useState<{ voce?: VoceFondo } | null>(null)
  const [erroreAzione, setErroreAzione] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState<string | null>(null)
  const gestisce = puo('catalogo.gestisci')

  async function cambiaAttivo(voce: VoceFondo, attivo: boolean) {
    setErroreAzione(null)
    setInCorso(voce.codice)
    try {
      await api.patch(`/fondi/${encodeURIComponent(voce.codice)}`, { attivo })
      setVersione((n) => n + 1)
    } catch (e) {
      setErroreAzione(messaggioDi(e, 'Operazione riservata all’amministratore.'))
    } finally {
      setInCorso(null)
    }
  }

  return (
    <>
      <Intestazione
        titolo="Catalogo dei fondi"
        sottotitolo="Voci del campo Fondo nell’anagrafica dei progetti."
        azioni={<>
          {puo('progetto.leggi') && <Link className="bottone bottone-discreto" to="/progetti">Progetti</Link>}
          {gestisce && (
            <Bottone variante="primario" icona={<IconaPiu />} onClick={() => setModulo({})}>Aggiungi una voce</Bottone>
          )}
        </>}
      />
      {!gestisce && <Avviso tipo="informazione">Il catalogo si consulta; le voci le modifica l’amministratore.</Avviso>}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {erroreAzione && <Avviso tipo="errore">{erroreAzione}</Avviso>}
      {!errore && !voci && <Attesa />}
      {voci && (
        <Riquadro denso>
          <Tabella intestazioni={['Voce', 'Codice', 'Specifica richiesta', 'Stato',
                                  ...(gestisce ? [<span className="solo-lettore" key="azioni">Azioni</span>] : [])]}
                   vuoto={<Vuoto testo="Il catalogo è vuoto." />}>
            {voci.map((f) => (
              <tr key={f.codice}>
                <td data-etichetta="Voce">{f.nome}</td>
                <td data-etichetta="Codice" className="mono">{f.codice}</td>
                <td data-etichetta="Specifica richiesta">{f.richiede_testo ? 'sì' : 'no'}</td>
                <td data-etichetta="Stato">
                  {f.attivo ? <Etichetta tono="positivo">in uso</Etichetta> : <Etichetta>non più in uso</Etichetta>}
                </td>
                {gestisce && (
                  <td data-etichetta="Azioni">
                    <span className="riga riga-a-destra">
                      <Bottone variante="discreto" dimensione="piccolo" onClick={() => setModulo({ voce: f })}>Modifica</Bottone>
                      <Bottone variante="discreto" dimensione="piccolo" inCorso={inCorso === f.codice}
                               onClick={() => void cambiaAttivo(f, !f.attivo)}>
                        {f.attivo ? 'Disattiva' : 'Riattiva'}
                      </Bottone>
                    </span>
                  </td>
                )}
              </tr>
            ))}
          </Tabella>
        </Riquadro>
      )}
      {modulo && (
        <Modale titolo={modulo.voce ? 'Modifica la voce' : 'Aggiungi una voce al catalogo'} chiudi={() => setModulo(null)}>
          <ModuloFondo voce={modulo.voce} annulla={() => setModulo(null)}
                       salvato={() => { setModulo(null); setVersione((n) => n + 1) }} />
        </Modale>
      )}
    </>
  )
}

function ModuloFondo({ voce, salvato, annulla }: { voce?: VoceFondo; salvato: () => void; annulla: () => void }) {
  const [codice, setCodice] = useState(voce?.codice ?? '')
  const [nome, setNome] = useState(voce?.nome ?? '')
  const [richiedeTesto, setRichiedeTesto] = useState(voce?.richiede_testo ?? false)
  const [ordinamento, setOrdinamento] = useState(String(voce?.ordinamento ?? 50))
  const [errori, setErrori] = useState<Record<string, string>>({})
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  async function salva() {
    const e: Record<string, string> = {}
    const codiceMaiuscolo = codice.trim().toUpperCase()
    if (!voce && !/^[A-Z0-9_]{2,40}$/.test(codiceMaiuscolo)) e.codice = 'Da 2 a 40 caratteri: lettere, cifre e trattino basso.'
    if (nome.trim().length < 2) e.nome = 'Indicare l’etichetta della voce.'
    if (!/^\d{1,5}$/.test(ordinamento.trim())) e.ordinamento = 'Un numero intero.'
    setErrori(e)
    if (Object.keys(e).length) { setErrore('Correggere i campi segnalati.'); return }
    setErrore(null)
    setInCorso(true)
    try {
      if (voce) {
        const corpo: Record<string, unknown> = {}
        if (nome.trim() !== voce.nome) corpo.nome = nome.trim()
        if (richiedeTesto !== voce.richiede_testo) corpo.richiede_testo = richiedeTesto
        if (Number(ordinamento) !== voce.ordinamento) corpo.ordinamento = Number(ordinamento)
        if (Object.keys(corpo).length) await api.patch(`/fondi/${encodeURIComponent(voce.codice)}`, corpo)
      } else {
        await api.post('/fondi', {
          codice: codiceMaiuscolo, nome: nome.trim(), richiede_testo: richiedeTesto, ordinamento: Number(ordinamento),
        })
      }
      salvato()
    } catch (err) {
      setErrore(messaggioDi(err, 'Operazione riservata all’amministratore.'))
    } finally {
      setInCorso(false)
    }
  }

  return (
    <form onSubmit={(ev) => { ev.preventDefault(); void salva() }} noValidate>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <div className="griglia-modulo">
        <Campo etichetta="Codice" obbligatorio={!voce} disabilitato={!!voce} maxLength={40} valore={codice}
               cambia={setCodice} errore={errori.codice}
               aiuto={voce ? 'Il codice non cambia: lo usano i progetti.' : 'In maiuscolo, per esempio FONDO_ASILO.'} />
        <Campo etichetta="Etichetta" obbligatorio maxLength={200} valore={nome} cambia={setNome} errore={errori.nome} />
        <Campo etichetta="Posizione nell’elenco" tipo="number" min={0} max={32000} modalitaTastiera="numeric"
               valore={ordinamento} cambia={setOrdinamento} errore={errori.ordinamento} />
      </div>
      <Spunta etichetta="Chiede una specifica in testo (come la voce «Altro»)" valore={richiedeTesto} cambia={setRichiedeTesto} />
      <div className="azioni-modulo">
        <Bottone variante="discreto" onClick={annulla}>Annulla</Bottone>
        <Bottone variante="primario" tipo="submit" inCorso={inCorso}>{voce ? 'Salva la voce' : 'Aggiungi la voce'}</Bottone>
      </div>
    </form>
  )
}
